import { Block, EntityItem, Page } from '@epilot/customer-portal-client'
import { isEmpty } from 'lodash'
import React, { createContext, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { getCustomerApiClient } from 'api/CustomerApiClient'
import { CUSTOMER_QUERY_IDS } from 'api/Queries'
import { isInDummyMode, useQueryWithMock } from 'dummy/static'
import { useContextEntities } from 'hooks/common/useContextEntities'
import { findPageByPath, generateRoutes } from 'routes/routeGenerator'
import { useIsFeatureSettingsEnabled } from 'utils/FeatureFlagUtils'
import { getPortalDomain } from 'utils/portal-config'

import AuthContext, { hasLoginAsToken } from './AuthContext'
import PortalConfigContext from './PortalConfigContext'

const initialState: PageConfigContextType = {
  data: {
    pages: [],
    publicPages: [],
    selectedPage: {} as Page,
    selectedEntity: null as EntityItem,
    contractId: null
  },
  actions: {
    setSelectedEntity: () => {}
  },
  isLoading: false,
  isError: false,
  isFetching: false,
  isPreviousData: false,
  isCookieConsentDialogOpen: false,
  setIsCookieConsentDialogOpen: () => {}
}

type PageConfigContextType = {
  data: {
    pages: Page[]
    publicPages: Page[]
    selectedPage: Page
    selectedEntity: EntityItem
    contractId: string | null
  }
  actions: {
    setSelectedEntity: (entity: EntityItem) => void
  }
  isLoading: boolean
  isError: boolean
  /** True when pages are being refetched (e.g., due to context entity changes) */
  isFetching: boolean
  /** True when showing stale data from a different context while fetching new data */
  isPreviousData: boolean
  isCookieConsentDialogOpen: boolean
  setIsCookieConsentDialogOpen: (isOpen: boolean) => void
}

const PageConfigContext = createContext<PageConfigContextType>(initialState)

const PageConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const { data: portalConfigData } = useContext(PortalConfigContext)
  const { data: authData } = useContext(AuthContext)
  const [isCookieConsentDialogOpen, setIsCookieConsentDialogOpen] =
    React.useState<boolean>(false)

  const [selectedEntity, setSelectedEntity] = React.useState<EntityItem | null>(
    null
  )

  const domain = getPortalDomain(portalConfigData?.portalConfig)
  const isNewDesignEnabled =
    useIsFeatureSettingsEnabled('NEW_DESIGN') || !!window.AppShell

  // Get allowed schemas from portal config for dynamic context entity support
  const allowedSchemas = portalConfigData?.portalConfig?.allowed_portal_entities

  const { contractId, contextEntities } = useContextEntities({
    allowedSchemas
  })

  const {
    data: pagesData,
    isLoading,
    isFetching,
    isPreviousData,
    isError: isPagesError
  } = useQueryWithMock({
    queryKey: [
      CUSTOMER_QUERY_IDS.GET_PAGES,
      domain,
      JSON.stringify(contextEntities)
    ],
    queryFn: () =>
      getCustomerApiClient().getPortalPages({
        domain,
        context_entities: contextEntities
      }),
    select: ({ data }) => {
      const activePagesOnly = data?.filter((p) => !p?.is_deleted)
      const isApp = Boolean(window.AppShell)
      const filteredPages = isApp
        ? activePagesOnly?.filter((p) => !p?.visibility?.hidden_in_app)
        : activePagesOnly

      return (
        generateRoutes(isInDummyMode() ? activePagesOnly : filteredPages) || []
      )
    },
    enabled:
      !!domain &&
      isNewDesignEnabled &&
      ((authData.authenticated && !hasLoginAsToken()) || isInDummyMode()),
    keepPreviousData: true
  })

  const token = portalConfigData.portalConfig.accessToken
  const headers = token
    ? { headers: { authorization: `Bearer ${token}` } }
    : null

  const {
    data: publicPagesData,
    isLoading: isPublicPagesLoading,
    isFetching: isPublicPagesFetching,
    isError: isPublicPagesError
  } = useQueryWithMock({
    queryKey: [CUSTOMER_QUERY_IDS.GET_PUBLIC_PAGES, domain],
    queryFn: () =>
      getCustomerApiClient().getPublicPages({ domain }, null, headers),
    select: ({ data }) => {
      const activePagesOnly = data?.filter((p) => !p?.is_deleted)
      const isApp = Boolean(window.AppShell)
      const filteredPages = isApp
        ? activePagesOnly?.filter((p) => !p?.visibility?.hidden_in_app)
        : activePagesOnly

      return (
        generateRoutes(isInDummyMode() ? activePagesOnly : filteredPages) || []
      )
    },
    enabled: !!domain && isNewDesignEnabled
  })

  const contextValue = useMemo(() => {
    const pages = pagesData ?? []
    const publicPages = publicPagesData ?? []

    return {
      data: {
        pages,
        publicPages,
        selectedPage: findPageByPath(
          pagesData ?? publicPagesData,
          location.pathname
        ),
        selectedEntity,
        contractId
      },
      actions: { setSelectedEntity },
      isLoading:
        (isLoading && isFetching) ||
        (isPublicPagesLoading && isPublicPagesFetching),
      isError: isPagesError || isPublicPagesError,
      isFetching: isFetching || isPublicPagesFetching,
      isPreviousData,
      isCookieConsentDialogOpen,
      setIsCookieConsentDialogOpen
    }
  }, [
    pagesData,
    publicPagesData,
    selectedEntity,
    contractId,
    location.pathname,
    isCookieConsentDialogOpen,
    isFetching,
    isLoading,
    isPreviousData,
    isPublicPagesFetching,
    isPublicPagesLoading,
    isPagesError,
    isPublicPagesError,
    setIsCookieConsentDialogOpen,
    setSelectedEntity
  ])

  return (
    <PageConfigContext.Provider value={contextValue}>
      {children}
    </PageConfigContext.Provider>
  )
}

const usePageConfig = () => {
  const context = useContext(PageConfigContext)

  if (!context) {
    throw new Error('PageConfigContext not found')
  }

  return context
}

const usePageBlockByType = (blockType: Block['type']): Block | null => {
  const { data: pageConfigData } = usePageConfig()

  const selectorBlock = useMemo(() => {
    if (isEmpty(pageConfigData?.selectedPage?.blocks)) {
      return null
    }

    const blocks = Object.values(pageConfigData?.selectedPage?.blocks)

    return blocks.find((block) => block.type === blockType) ?? null
  }, [pageConfigData?.selectedPage?.blocks, blockType])

  return selectorBlock
}

export {
  PageConfigProvider,
  PageConfigContext,
  usePageConfig,
  usePageBlockByType
}
